// src/store/modules/formWizards/mtsTakipHazirla.ts
import {ActionTree, GetterTree, MutationTree} from 'vuex';
import Vue from 'vue';
import {MtsDosyaFormData} from '@/plugins/uyap-plugin/takip_ac/MtsDosyaFormData';
import {MtsXmlParser} from '@/plugins/uyap-plugin/takip_ac/MtsXmlParser';
import {TakipAcStatusEnum} from '@/plugins/uyap-plugin/takip_ac/parser/TakipAcStatusEnum';
import MtsTakipAc from '@/plugins/uyap-plugin/takip_ac/MtsTakipAc';
import app from '@/main';
import {Task} from '@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes';
import TarafKisi from "@/plugins/uyap-plugin/takip_ac/parser/TarafKisi";
import TarafKurum from "@/plugins/uyap-plugin/takip_ac/parser/TarafKurum";

export interface MtsTakipHazirlaState {
    // Form verileri
    formData: MtsDosyaFormData;

    // Dropdown verileri
    adliyeler: any[];
    avukatIbanlari: any[];
    avukatIbanlariLastUpdated: number | null;
    yetkiliKurumlar: any[];

    // Parse edilmiş dosyalar
    dosyalar: MtsTakipAc[];
    secilenDosyalar: MtsTakipAc[];

    // Validasyon hataları
    dosyaValidasyonHatalari: Array<{ tempId: number, message: string }>;

    // İşlem durumu
    isParsing: boolean;
    isSubmitting: boolean;
    isRunQuery: boolean;
    isCancelled: boolean;
    onFly: boolean;
    sorgulanmaDurumu: number;
    durumAciklamasi: string;

    // Vekalet cache
    vekaletCache: Array<{ hash: string; data: any }>;

    // Parent task ID
    parentTaskId: string | null;

    // Son parse edilen XML'in imzası (XML değişikliği kontrolü için)
    lastParsedXmlSignature: string;

    // Son parse edilen form verilerinin imzası (form değişikliği kontrolü için)
    lastParsedFormDataSignature: string;

    // DEV-3302: hash kaydı silinen satırların tempId'leri. Bileşen içinde tutulunca sayfa değişince
    // çöp kutusu geri geliyordu; satır nesnesine bayrak konmaz çünkü task.data olarak Mongo'ya yazılır.
    hashSilinenTempIdler: number[];
}

const state: MtsTakipHazirlaState = {
    formData: new MtsDosyaFormData(),
    adliyeler: [],
    avukatIbanlari: [],
    avukatIbanlariLastUpdated: null,
    yetkiliKurumlar: [],
    dosyalar: [],
    secilenDosyalar: [],
    dosyaValidasyonHatalari: [],
    isParsing: false,
    isSubmitting: false,
    isRunQuery: false,
    isCancelled: false,
    onFly: false,
    sorgulanmaDurumu: 0,
    durumAciklamasi: '',
    vekaletCache: [],
    parentTaskId: null,
    lastParsedXmlSignature: '',
    lastParsedFormDataSignature: '',
    hashSilinenTempIdler: [],
};

const mutations: MutationTree<MtsTakipHazirlaState> = {
    SET_FORM_DATA(state, formData: Partial<MtsDosyaFormData>) {
        state.formData = {...state.formData, ...formData};
    },
    SET_ADLIYELER(state, adliyeler: any[]) {
        state.adliyeler = adliyeler;
    },
    SET_AVUKAT_IBANLARI(state, ibanlari: any[]) {
        state.avukatIbanlari = ibanlari;
        state.avukatIbanlariLastUpdated = Date.now();
    },
    SET_YETKILI_KURUMLAR(state, kurumlar: any[]) {
        state.yetkiliKurumlar = kurumlar;
    },
    SET_DOSYALAR(state, dosyalar: MtsTakipAc[]) {
        if (state.dosyalar !== dosyalar) {
            state.dosyalar = dosyalar;
            if (state.secilenDosyalar.length) {
                const selectedIds = new Set(
                    state.secilenDosyalar.map(d => d.tempId).filter(id => id != null)
                );
                state.secilenDosyalar = dosyalar.filter(d => selectedIds.has(d.tempId));
            }
            // DEV-3302: yeni XML yüklenince liste boşaltılır ve tempId'ler başka satırlara verilir;
            // hash silindi işaretleri de sıfırlanır. Diğer çağrılar (addVekaletToAll, addDayanakToAll)
            // aynı nesnelerle yeni dizi gönderdiği için işaretler korunur.
            if (!dosyalar.length)
                state.hashSilinenTempIdler = [];
        }
    },
    SET_SECILEN_DOSYALAR(state, dosyalar: MtsTakipAc[]) {
        state.secilenDosyalar = dosyalar;
    },
    SET_IS_PARSING(state, isParsing: boolean) {
        state.isParsing = isParsing;
    },
    SET_ON_FLY(state, onFly: boolean) {
        state.onFly = onFly;
    },
    SET_DURUM_ACIKLAMASI(state, durumAciklamasi: string) {
        state.durumAciklamasi = durumAciklamasi;
    },
    SET_IS_CANCELLED(state, value: boolean) {
        state.isCancelled = value;
    },
    SET_VEKALET_CACHE(state, cache: Array<{ hash: string; data: any }>) {
        state.vekaletCache = cache;
    },
    ADD_VEKALET_CACHE(state, cacheItem: { hash: string; data: any }) {
        state.vekaletCache.push(cacheItem);
    },
    SET_PARENT_TASK_ID(state, parentTaskId: string | null) {
        state.parentTaskId = parentTaskId;
    },
    SET_IS_SUBMITTING(state, isSubmitting: boolean) {
        state.isSubmitting = isSubmitting;
        // DEV-3302: yeniden çalıştırılan satırın hash'i tekrar kaydedilebilir; yalnız onların işareti kalkar.
        if (isSubmitting) {
            const calisacaklar = state.secilenDosyalar.map(d => d.tempId);
            state.hashSilinenTempIdler = state.hashSilinenTempIdler.filter(id => !calisacaklar.includes(id));
        }
    },
    HASH_SILINDI(state, tempId: number) {
        if (!state.hashSilinenTempIdler.includes(tempId))
            state.hashSilinenTempIdler = [...state.hashSilinenTempIdler, tempId];
    },
    SET_IS_RUN_QUERY(state, isRunQuery: boolean) {
        state.isRunQuery = isRunQuery;
    },
    SET_SORGULANMA_DURUMU(state, sorgulanmaDurumu: number) {
        state.sorgulanmaDurumu = sorgulanmaDurumu;
    },
    SET_DOSYA_VALIDASYON_HATALARI(state, hatalar: Array<{ tempId: number, message: string }>) {
        state.dosyaValidasyonHatalari = hatalar;
    },
    SET_LAST_PARSED_XML_SIGNATURE(state, signature: string) {
        state.lastParsedXmlSignature = signature;
    },
    SET_LAST_PARSED_FORM_DATA_SIGNATURE(state, signature: string) {
        state.lastParsedFormDataSignature = signature;
    },
    UPDATE_DOSYA_STATUS(state, {tempId, status}: { tempId: number; status: number }) {
        state.secilenDosyalar = state.secilenDosyalar.map(d => {
            if (d.tempId == tempId) {
                d.status = status;
                return d;
            } else {
                return d;
            }
        });
        state.dosyalar = state.dosyalar.map(d => {
            if (d.tempId == tempId) {
                d.status = status;
                return d;
            } else {
                return d;
            }
        });
    },
    RESET_STATE(state) {
        state.formData = new MtsDosyaFormData();
        state.adliyeler = [];
        state.avukatIbanlari = [];
        state.avukatIbanlariLastUpdated = null;
        state.yetkiliKurumlar = [];
        state.dosyalar = [];
        state.secilenDosyalar = [];
        state.dosyaValidasyonHatalari = [];
        state.isParsing = false;
        state.isSubmitting = false;
        state.isRunQuery = false;
        state.isCancelled = false;
        state.onFly = false;
        state.sorgulanmaDurumu = 0;
        state.durumAciklamasi = '';
        state.vekaletCache = [];
        state.parentTaskId = null;
        state.lastParsedXmlSignature = '';
        state.lastParsedFormDataSignature = '';
        state.hashSilinenTempIdler = [];
    },
};

// Helper functions
function getValidationRules(actionType: 'kuyrugaAktarTevziAl' | 'kuyrugaAktarTevziAlVeAc' | 'hemenAcTevziAl' | 'hemenAcTevziAlVeAc') {
    switch (actionType) {
        case 'kuyrugaAktarTevziAl':
            return {
                requireVekalet: true,
                checkDayanak: true,
                maxDosyaSayisi: undefined
            };
        case 'kuyrugaAktarTevziAlVeAc':
            return {
                requireVekalet: true,
                checkDayanak: true,
                maxDosyaSayisi: undefined
            };
        case 'hemenAcTevziAl':
            return {
                requireVekalet: true,
                checkDayanak: true,
                maxDosyaSayisi: 5
            };
        case 'hemenAcTevziAlVeAc':
            return {
                requireVekalet: true,
                checkDayanak: true,
                maxDosyaSayisi: 5
            };
    }
}

function dayanakIsRequired(): boolean {
    return true;
}

function sirketBilgisiKontrol(secilenDosyalar: MtsTakipAc[]): {
    success: boolean;
    messages?: string[]
} {
    const eksikSirketBilgisi: string[] = [];

    for (const dosya of secilenDosyalar) {
        if (!dosya.parsedFile?.tarafList) {
            continue;
        }

        // TODO: bu kontrol belki kalkabilir sorulacak
        // Alacaklı şirket kontrolü
        const alacaklilar = dosya.parsedFile.tarafList.filter(
            (taraf: any) => taraf.isAlacakli()
        );

        for (const alacakli of alacaklilar) {
            // Şirket ise (kişi değilse) ve kurum adı yoksa
            if (!alacakli.kisiMi) {
                const kurumAdi = alacakli.temelBilgileri?.kurumAdi || alacakli.kurumAdi;
                if (!kurumAdi) {
                    eksikSirketBilgisi.push(
                        `Dosya #${dosya.tempId}: Alacaklı şirket bilgisi bulunamadı`
                    );
                }
            }
        }

        // Borçlu şirket kontrolü
        const borclular = dosya.parsedFile.tarafList.filter(
            (taraf: any) => taraf.isBorclu()
        );

        for (const borclu of borclular) {
            // Şirket ise (kişi değilse) ve kurum adı yoksa
            if (!borclu.kisiMi) {
                const kurumAdi = borclu.temelBilgileri?.kurumAdi || borclu.kurumAdi;
                if (!kurumAdi) {
                    eksikSirketBilgisi.push(
                        `Dosya #${dosya.tempId}: Borçlu şirket bilgisi bulunamadı`
                    );
                }
            }
        }
    }

    if (eksikSirketBilgisi.length > 0) {
        return {
            success: false,
            messages: eksikSirketBilgisi
        };
    }

    return {success: true};
}

const actions: ActionTree<MtsTakipHazirlaState, any> = {
    updateFormData({commit}, formData: Partial<MtsDosyaFormData>) {
        commit('SET_FORM_DATA', formData);
    },
    async fetchAdliyeler({commit}, il: string) {
        if (!il) {
            commit('SET_ADLIYELER', []);
            return [];
        }

        try {
            const response = await app.$uyap.Adliyeler().run({ilKodu: il});
            const adliyeler = Array.isArray(response) ? response : [];
            commit('SET_ADLIYELER', adliyeler);
            return adliyeler;
        } catch (error) {
            console.error('Adliyeleri yüklenirken hata:', error);
            commit('SET_ADLIYELER', []);
            return [];
        }
    },
    async fetchAvukatIbanlari({commit, state}) {
        // 30dk cache
        if (state.avukatIbanlariLastUpdated && Date.now() - state.avukatIbanlariLastUpdated < 30 * 60 * 1000)
            return state.avukatIbanlari;

        try {
            const response = await app.$uyap.AvukatKayitliIbanList().run({});
            const ibanlari = response.value || [];
            commit('SET_AVUKAT_IBANLARI', ibanlari);
            return ibanlari;
        } catch (error) {
            console.error('Avukat IBAN\'ları yüklenirken hata:', error);
            commit('SET_AVUKAT_IBANLARI', []);
            return [];
        }
    },
    async fetchYetkiliKurumlar({commit, state}) {
        // Cache kontrolü - eğer zaten yüklenmişse tekrar yükleme
        if (state.yetkiliKurumlar && state.yetkiliKurumlar.length > 0) {
            return state.yetkiliKurumlar;
        }

        try {
            const response = await app.$uyap.AvukatYetkiliKurumlar().run({});
            const kurumlar = Array.isArray(response) ? response : [];
            commit('SET_YETKILI_KURUMLAR', kurumlar);
            return kurumlar;
        } catch (error) {
            console.error('Yetkili kurumlar yüklenirken hata:', error);
            commit('SET_YETKILI_KURUMLAR', []);
            return [];
        }
    },
    setDosyalar({commit}, dosyalar: MtsTakipAc[]) {
        commit('SET_DOSYALAR', dosyalar);
    },
    setSecilenDosyalar({commit}, dosyalar: MtsTakipAc[]) {
        commit('SET_SECILEN_DOSYALAR', dosyalar);
    },
    setIsParsing({commit}, isParsing: boolean) {
        commit('SET_IS_PARSING', isParsing);
    },
    setOnFly({commit}, onFly: boolean) {
        commit('SET_ON_FLY', onFly);
    },
    setDurumAciklamasi({commit}, durumAciklamasi: string) {
        commit('SET_DURUM_ACIKLAMASI', durumAciklamasi);
    },
    addVekaletCache({commit}, cacheItem: { hash: string; data: any }) {
        commit('ADD_VEKALET_CACHE', cacheItem);
    },
    setParentTaskId({commit}, parentTaskId: string | null) {
        commit('SET_PARENT_TASK_ID', parentTaskId);
    },
    resetState({commit}) {
        commit('RESET_STATE');
    },

    async addVekaletToAll({commit, state}, vekalet: any) {
        // OfisPro'dan seçilen vekalet içeriksiz kayıttır, indirilmek üzere vekaletObject'e gider (DEV-4188).
        const alan = vekalet.id && !vekalet.data ? 'vekaletObject' : 'vekalet';
        const updatedDosyalar = state.dosyalar.map(dosya => {
            if (dosya.parsedFile && !dosya.parsedFile.vekalet && !dosya.parsedFile.vekaletObject) {
                Vue.set(dosya.parsedFile, alan, vekalet);
            }
            return dosya;
        });
        commit('SET_DOSYALAR', updatedDosyalar);
    },

    async addDayanakToAll({commit, state}, dayanaklar: any[]) {
        const updatedDosyalar = state.dosyalar.map(dosya => {
            if (!dosya.parsedFile) return dosya;
            if (!dosya.parsedFile.dayanaklar) {
                Vue.set(dosya.parsedFile, 'dayanaklar', []);
            }
            const existingDayanaklar = dosya.parsedFile.dayanaklar || [];
            const newDayanaklar = [...existingDayanaklar, ...dayanaklar];
            Vue.set(dosya.parsedFile, 'dayanaklar', newDayanaklar);
            return dosya;
        });
        commit('SET_DOSYALAR', updatedDosyalar);
    },

    async takipGonderimKontrolleri(
        {state, getters},
        options: {
            actionType: 'kuyrugaAktarTevziAl' | 'kuyrugaAktarTevziAlVeAc' | 'hemenAcTevziAl' | 'hemenAcTevziAlVeAc'
        }
    ): Promise<{ success: boolean; message: string }> {

        // Senaryo-spesifik validasyon kurallarını belirle
        const validationRules = getValidationRules(options.actionType);

        // 1. Dosya seçimi kontrolü
        const secilenDosyalar = getters.secilenDosyalar;
        if (secilenDosyalar.length === 0) {
            return {
                success: false,
                message: 'Hazırlanacak takipleri seçiniz.'
            };
        }

        // 2. Dosya sayısı kontrolü (Hemen Aç için)
        if (validationRules.maxDosyaSayisi &&
            secilenDosyalar.length > validationRules.maxDosyaSayisi) {
            return {
                success: false,
                message: `Bu işlem için maksimum ${validationRules.maxDosyaSayisi} dosya seçebilirsiniz.`
            };
        }

        // 3. Borçlu seçimi kontrolü - MTS'de takipeDahilEt kullanılıyor
        const hasSelectedBorclu = secilenDosyalar.some((dosya: MtsTakipAc) => {
            return dosya.parsedFile?.tarafList
                ?.filter((taraf: any) => taraf.isBorclu())
                ?.some((borclu: any) => borclu.selected === true);
        });

        if (!hasSelectedBorclu) {
            return {
                success: false,
                message: 'En az bir borçlu seçmelisiniz.'
            };
        }

        // Alacaklı kontrolü - MTS'de alacaklılar her zaman dahil edilir
        const hasAlacakli = secilenDosyalar.some((dosya: MtsTakipAc) => {
            return dosya.parsedFile?.tarafList?.some((taraf: any) => taraf.isAlacakli());
        });

        if (!hasAlacakli) {
            return {
                success: false,
                message: 'En az bir alacaklı olmalıdır.'
            };
        }

        // TODO: mahiyet olmadan zaten ilerlenmiyro diye biliyorum
        // 4. Mahiyet kontrolü - MTS'de formData.mahiyetId kontrol edilir
        const mahiyetCheck = state.formData.mahiyetId && state.formData.mahiyetId > 0;

        if (!mahiyetCheck) {
            return {
                success: false,
                message: 'Mahiyet seçimi eksik. Lütfen mahiyet seçiniz.'
            };
        }

        // 5. Vekalet kontrolü (senaryoya göre - ZORUNLU ise)
        if (validationRules.requireVekalet) {
            const vekaletEksikDosyalar = secilenDosyalar.filter((dosya: MtsTakipAc) => {
                return !dosya.parsedFile?.vekalet && !dosya.parsedFile?.vekaletObject;
            });

            if (vekaletEksikDosyalar.length > 0) {
                return {
                    success: false,
                    message: 'Vekalet eksik. Bu işlemde tüm dosyalar için vekalet gereklidir.'
                };
            }
        }

        // 6. Şirket bilgisi kontrolü (TÜM senaryolar için)
        const sirketValidation = sirketBilgisiKontrol(secilenDosyalar);
        if (!sirketValidation.success) {
            return {
                success: false,
                message: sirketValidation.messages?.join('\n') || 'Şirket bilgisi eksik'
            };
        }

        // 7. Dayanak kontrolü
        if (validationRules.checkDayanak) {
            const dayanakEksikDosyalar: number[] = [];

            secilenDosyalar.forEach((dosya: MtsTakipAc) => {
                if (dayanakIsRequired()) {
                    // Dayanak zorunlu ve yoksa veya boşsa
                    if (!dosya.parsedFile?.dayanaklar || dosya.parsedFile.dayanaklar.length === 0) {
                        dayanakEksikDosyalar.push(dosya.tempId);
                    }
                }
            });

            if (dayanakEksikDosyalar.length > 0) {
                return {
                    success: false,
                    message: `Bazı dosyalar için dayanak belgesi zorunludur. Dayanak gerektiren dosyalar: #${dayanakEksikDosyalar.join(', #')}`
                };
            }
        }

        // 8. XML parsing hataları kontrolü
        const validasyonHatalari = getters.dosyaValidasyonHatalari || [];
        if (validasyonHatalari.length > 0) {
            return {
                success: false,
                message: 'XML dosyası işlenirken hata oluştu:\n' + validasyonHatalari.map((h: any) => h.message).join('\n')
            };
        }

        return {success: true, message: 'Kontrol başarılı.'};
    },

    /**
     * UTILITY ACTION: Vekalet upload - Senkron ile, cache ile
     */
    async uploadVekaletWithSync({commit, state}, {dosya}: { dosya: MtsTakipAc }) {
        if (!dosya.parsedFile?.vekalet || !dosya.parsedFile.vekalet.data) {
            throw new Error(`Dosya #${dosya.tempId} için vekalet data bulunamadı`);
        }

        const vekalet = dosya.parsedFile.vekalet.data;
        const Crypto = require('crypto');
        const hash = Crypto.createHash('sha1').update(vekalet).digest().toString('hex');
        const vekaletCache = state.vekaletCache;
        const findCache = vekaletCache.find((cache: any) => cache.hash == hash);

        if (findCache)
            return findCache.data;

        const formData = new FormData();
        const vekaletBlob = app.$helper.base64toBlob(vekalet, dosya.parsedFile.vekalet.mimeType);
        const tarafList = dosya.parsedFile.tarafList.filter((taraf: TarafKisi | TarafKurum) => taraf.isAlacakli()) || [];

        // mtsde vekillist yok
        formData.append('vekilList', "[]");
        formData.append('tarafList', JSON.stringify(tarafList));
        formData.append('aciklama', dosya.parsedFile.vekalet.name);
        formData.append('name', dosya.parsedFile.vekalet.name);
        formData.append('file', vekaletBlob, dosya.parsedFile.vekalet.name);
        formData.append('dokuman_turu_id', '1');
        formData.append('hash', hash);
        formData.append('mime_type', dosya.parsedFile.vekalet.mimeType);
        formData.append('is_mts', 'true');

        const vekaletData = await app.$http.post('/api/v1/task/vekalet-senkron', formData);
        const normalized = vekaletData?.file ?? vekaletData;
        commit('ADD_VEKALET_CACHE', {hash: hash, data: normalized});
        return normalized;
    },

    /**
     * UTILITY ACTION: Dayanak upload - BLOB, HASH
     */
    async uploadDayanaklar({commit}, payload: any) {
        // Geriye dönük uyumluluk: doğrudan parsedFile gelebilir
        const parsedFile = payload?.parsedFile ?? payload;
        const dosyaNo = payload?.dosyaNo;

        const dayanaklarObject: any[] = [];
        const dayanaklarList = parsedFile.dayanaklar || [];

        for (const [j, dayanak] of dayanaklarList.entries()) {
            try {
                if (dosyaNo != null) {
                    commit('SET_DURUM_ACIKLAMASI',
                        `#${dosyaNo} için dayanaklar yükleniyor... (${j + 1}/${dayanaklarList.length} dayanak)`
                    );
                }

                const Crypto = require('crypto');
                const base64 = dayanak.data;
                const hash = Crypto.createHash('sha1').update(base64).digest().toString('hex');

                const formData = new FormData();
                const blob = app.$helper.base64toBlob(base64, dayanak.mimeType);
                formData.append('aciklama', dayanak.name);
                formData.append('name', dayanak.name);
                formData.append('file', blob, dayanak.name);
                formData.append('hash', hash);
                formData.append('dokuman_turu_id', "9");
                formData.append('mime_type', dayanak.mimeType);
                const dayanakData = await app.$http.post('/api/v1/file', formData);
                dayanaklarObject.push(dayanakData.data);
            } catch (error) {
                console.error(`Dayanak upload hatası (${dayanak.name}):`, error);
                throw error;
            }
        }

        return dayanaklarObject;
    },

    /**
     * UTILITY ACTION: MTS Takip hazırla task'ları oluştur
     */
    async createMtsTakipHazirlaTasks({commit, state, dispatch}, {dosyalar, taskType, xmlName}: {
        dosyalar: MtsTakipAc[];
        taskType: any;
        xmlName: string;
    }) {
        try {
            const tasks: any[] = [];
            console.log("createMtsTakipHazirlaTasks", [...dosyalar]);
            const hazirDosyalar = dosyalar.filter((dosya: MtsTakipAc) => {
                return dosya.parsedFile && (dosya.status == TakipAcStatusEnum.OPEN_READY);
            });
            console.log("createMtsTakipHazirlaTasks hazirDosyalar",hazirDosyalar);

            const toplamHazir = hazirDosyalar.length;

            for (const [idx, dosya] of hazirDosyalar.entries()) {
                const parsedFile = dosya.parsedFile;

                // Ekran ortası ilerleme barı: hangi dosyada / kaç dosya kaldı bilgisi
                commit('SET_DURUM_ACIKLAMASI',
                    `#${dosya.tempId} için dayanaklar hazırlanıyor... (${idx + 1}/${toplamHazir})`
                );
                commit('SET_SORGULANMA_DURUMU', Math.round((idx / toplamHazir) * 100));

                // Borçlu filtreleme - Sadece seçili borçluları tut
                if (parsedFile && parsedFile.tarafList) {
                    parsedFile.tarafList = parsedFile.tarafList.filter((taraf: any) => {
                        if (!taraf || !taraf.tarafSifati) {
                            return false; // Geçersiz taraf, filtrele
                        }
                        if (taraf.isAlacakli()) {
                            return true; // Always include alacaklılar
                        } else {
                            return taraf.takipeDahilEt === true; // Only selected borçlular
                        }
                    });
                }

                // Dayanak upload (her dayanak yüklenirken bar güncellenir)
                const dayanaklar = await dispatch('uploadDayanaklar', {parsedFile: dosya.parsedFile, dosyaNo: dosya.tempId});
                parsedFile.dayanaklar = [];
                parsedFile.dayanaklarObject = dayanaklar;

                // @ts-ignore
                dosya.xmlName = xmlName;

                const task: Task = new Task();
                task.parent_id = state.parentTaskId;
                task.task_type_id = taskType;
                task.data = dosya;
                tasks.push(task);
            }

            console.log("*** TASKS = ",tasks);

            // Task'ları oluştur
            const response = await app.$store.dispatch('postTasks', tasks);

            if (response && response.data?.id) {
                commit('SET_PARENT_TASK_ID', response.data.id);
                return {success: true, parentTaskId: response.data.id};
            } else {
                return {success: false, error: 'Task oluşturulamadı'};
            }
        } catch (error) {
            console.error('createMtsTakipHazirlaTasks hatası:', error);
            return {success: false, error};
        }
    },

    /**
     * SENARYO 1: HEMEN AÇ - TEVZİ AL
     */
    async executeSenaryo1_HemenAcTevziAl({commit, state}) {
        try {
            commit('SET_IS_SUBMITTING', true);
            commit('SET_SORGULANMA_DURUMU', 0);
            commit('SET_DOSYA_VALIDASYON_HATALARI', []); // Hataları temizle
            commit('SET_IS_CANCELLED', false); // Cancel reset

            let basariliSayisi = 0;
            let hataliSayisi = 0;
            const hataMesajlari: Array<{ tempId: number, message: string }> = [];
            const toplamDosyaSayisi = state.secilenDosyalar.length;

            for (const [i, dosya] of state.secilenDosyalar.entries()) {
                // İPTAL KONTROLÜ
                if (state.isCancelled) {
                    commit('SET_DURUM_ACIKLAMASI', 'İşlem kullanıcı tarafından iptal edildi');
                    break;
                }

                const dosyaNo = dosya.tempId;

                try {
                    // Status güncelle
                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.OPEN_PROCESSING // tevzi_isleniyor
                    });

                    commit('SET_DURUM_ACIKLAMASI',
                        `#${dosyaNo} dosya için tevzi alınıyor... (${i + 1}/${toplamDosyaSayisi})`
                    );
                    commit('SET_SORGULANMA_DURUMU', Math.round((i / toplamDosyaSayisi) * 100));

                    await dosya.takipBaslatYeni(true, false);

                    // Başarılı
                    commit('SET_DURUM_ACIKLAMASI', `#${dosyaNo} tevzi alındı ✓`);
                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.OPEN_SUCCESS // tevzi_basarili
                    });
                    basariliSayisi++;

                    commit('SET_SORGULANMA_DURUMU', Math.round(((i + 1) / toplamDosyaSayisi) * 100));
                } catch (error: any) {
                    console.error(`senaryo1 ${dosyaNo} dosya error:`, error);

                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.OPEN_FAIL // tevzi_hatali
                    });

                    const errorMsg = error.message ? error.message : String(error);
                    const hataText = `Dosya #${dosyaNo}: ${errorMsg}`;

                    hataMesajlari.push({tempId: dosyaNo, message: hataText});
                    commit('SET_DURUM_ACIKLAMASI', `#${dosyaNo} dosya hatası: ${errorMsg}`);
                    hataliSayisi++;

                    commit('SET_SORGULANMA_DURUMU', Math.round(((i + 1) / toplamDosyaSayisi) * 100));
                }
            }

            if (state.isCancelled) {
                return {
                    success: false,
                    cancelled: true,
                    message: `İşlem iptal edildi. (${basariliSayisi}/${toplamDosyaSayisi} dosya işlendi)`
                };
            }
            commit('SET_SORGULANMA_DURUMU', 100);

            // Başarı mesajı
            return {
                success: true,
                basariliSayisi,
                hataliSayisi,
                toplamDosyaSayisi,
                message: `Tevzi Al işlemi tamamlandı!\nBaşarılı: ${basariliSayisi}/${toplamDosyaSayisi}\nHatalı: ${hataliSayisi}/${toplamDosyaSayisi}`
            };
        } catch (error) {
            console.error("senaryo1 genel hata : ", error);
            throw error;
        } finally {
            commit('SET_IS_SUBMITTING', false);
            commit('SET_DURUM_ACIKLAMASI', '');
            commit('SET_SORGULANMA_DURUMU', 0);
            commit('SET_IS_CANCELLED', false);
        }
    },

    /**
     * SENARYO 2: HEMEN AÇ - TEVZİ AL VE AÇ
     */
    async executeSenaryo2_HemenAcTevziAlVeAc({commit, state}) {
        try {
            commit('SET_IS_SUBMITTING', true);
            commit('SET_SORGULANMA_DURUMU', 0);
            commit('SET_DOSYA_VALIDASYON_HATALARI', []); // Hataları temizle
            commit('SET_IS_CANCELLED', false); // Cancel reset

            let basariliSayisi = 0;
            let hataliSayisi = 0;
            const hataMesajlari: Array<{ tempId: number, message: string }> = [];
            const secilenDosyalar = state.secilenDosyalar;
            const toplamDosyaSayisi = secilenDosyalar.length;

            for (let i = 0; i < secilenDosyalar.length; i++) {
                // İPTAL KONTROLÜ
                if (state.isCancelled) {
                    commit('SET_DURUM_ACIKLAMASI', 'İşlem kullanıcı tarafından iptal edildi');
                    break;
                }

                const dosya = secilenDosyalar[i];
                const dosyaNo = dosya.tempId;

                try {
                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.OPEN_PROCESSING // tevzi_isleniyor
                    });

                    commit('SET_DURUM_ACIKLAMASI',
                        `#${dosyaNo} dosya için tevzi alınıyor ve takip açılıyor... (${i + 1}/${toplamDosyaSayisi})`
                    );
                    commit('SET_SORGULANMA_DURUMU', Math.round((i / toplamDosyaSayisi) * 100));

                    // Tevzi al + Takip aç + Ödeme yap
                    await dosya.takipBaslatYeni(true, true);

                    commit('SET_DURUM_ACIKLAMASI', `#${dosyaNo} takip açıldı ve ödeme yapıldı ✓`);
                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.OPEN_SUCCESS // takip_acildi
                    });
                    basariliSayisi++;

                    commit('SET_SORGULANMA_DURUMU', Math.round(((i + 1) / toplamDosyaSayisi) * 100));
                } catch (error) {
                    console.error(`senaryo2 ${dosyaNo} dosya error:`, error);

                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.OPEN_FAIL // tevzi_hatali
                    });

                    const errorMsg = error instanceof Error ? error.message : String(error);
                    const hataText = `Dosya #${dosyaNo}: ${errorMsg}`;

                    hataMesajlari.push({tempId: dosyaNo, message: hataText});
                    commit('SET_DURUM_ACIKLAMASI', `#${dosyaNo} dosya hatası: ${errorMsg}`);
                    hataliSayisi++;

                    commit('SET_SORGULANMA_DURUMU', Math.round(((i + 1) / toplamDosyaSayisi) * 100));
                }
            }

            if (state.isCancelled) {
                return {
                    success: false,
                    cancelled: true,
                    message: `İşlem iptal edildi. (${basariliSayisi}/${toplamDosyaSayisi} dosya işlendi)`
                };
            }

            commit('SET_SORGULANMA_DURUMU', 100);

            return {
                success: true,
                basariliSayisi,
                hataliSayisi,
                toplamDosyaSayisi,
                message: `Tevzi Al ve Aç işlemi tamamlandı!\nBaşarılı: ${basariliSayisi}/${toplamDosyaSayisi}\nHatalı: ${hataliSayisi}/${toplamDosyaSayisi}`
            };
        } catch (error) {
            console.error(`senaryo2 genel error:`, error);
            throw error;
        } finally {
            commit('SET_IS_SUBMITTING', false);
            commit('SET_DURUM_ACIKLAMASI', '');
            commit('SET_SORGULANMA_DURUMU', 0);
            commit('SET_IS_CANCELLED', false);
        }
    },

    /**
     * SENARYO 3 ve 4: KUYRUĞA AKTAR
     */
    async executeSenaryo34_KuyrugaAktar({commit, state, dispatch}, {actionType, taskTuru}: {
        actionType: string;
        taskTuru: any;
    }) {
        try {
            commit('SET_IS_SUBMITTING', true);
            commit('SET_IS_CANCELLED', false); // Cancel reset

            // Vekalet upload (varsa)
            for (let dosya of state.secilenDosyalar) {
                // İPTAL KONTROLÜ
                if (state.isCancelled) {
                    commit('SET_DURUM_ACIKLAMASI', 'İşlem kullanıcı tarafından iptal edildi');
                    break;
                }

                if (dosya.status != TakipAcStatusEnum.PREPARE_SUCCESS && dosya.status != TakipAcStatusEnum.OPEN_READY) {
                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.PREPARE_PROCESSING // isleniyor
                    });

                    // Vekalet opsiyonel - varsa upload yap
                    if (dosya.parsedFile?.vekalet && dosya.parsedFile.vekalet.data) {
                        commit('SET_DURUM_ACIKLAMASI', `#${dosya.tempId} dosyanın vekaleti kontrol ediliyor`);

                        try {
                            const vekaletData = await dispatch('uploadVekaletWithSync', {dosya});
                            if (dosya.parsedFile) {
                                dosya.parsedFile.vekaletObject = vekaletData;
                                delete dosya.parsedFile.vekalet;
                            }

                            commit('SET_DURUM_ACIKLAMASI', `#${dosya.tempId} dosya hazır`);
                        } catch (vekaletError) {
                            console.error('Vekalet kontrol hatası', vekaletError);
                            throw vekaletError;
                        }
                    } else if (dosya.parsedFile?.vekaletObject?.id) {
                        commit('SET_DURUM_ACIKLAMASI', `#${dosya.tempId} mevcut vekalet kullanılıyor`);
                    } else {
                        commit('SET_DURUM_ACIKLAMASI', `#${dosya.tempId} dosyanın vekaleti yok`);
                    }

                    commit('UPDATE_DOSYA_STATUS', {
                        tempId: dosya.tempId,
                        status: TakipAcStatusEnum.OPEN_READY // tamamlandi_hazir
                    });
                }
            }

            if (state.isCancelled) {
                return {
                    success: false,
                    cancelled: true,
                    message: 'İşlem iptal edildi'
                };
            }

            // Dayanak upload + Task oluştur
            const result = await dispatch('createMtsTakipHazirlaTasks', {
                dosyalar: state.secilenDosyalar,
                taskType: taskTuru,
                xmlName: state.formData.xml?.name || 'unknown.xml'
            });

            return {
                success: result.success,
                taskTuru,
                parentTaskId: result.parentTaskId,
                error: result.error
            };

        } catch (error) {
            console.error('handleKuyrugaAktar hatası', error);
            throw error;
        } finally {
            commit('SET_IS_SUBMITTING', false);
            commit('SET_IS_CANCELLED', false);
            commit('SET_DURUM_ACIKLAMASI', '');
            commit('SET_SORGULANMA_DURUMU', 0);
        }
    },
};

const getters: GetterTree<MtsTakipHazirlaState, any> = {
    formData: state => state.formData,
    adliyeler: state => state.adliyeler || [],
    avukatIbanlari: state => state.avukatIbanlari || [],
    yetkiliKurumlar: state => state.yetkiliKurumlar || [],
    dosyalar: state => state.dosyalar,
    secilenDosyalar: state => {
        // Infinite loop önleme: her zaman aynı referansı döndür
        return state.secilenDosyalar || [];
    },
    dosyaValidasyonHatalari: state => state.dosyaValidasyonHatalari,
    isParsing: state => state.isParsing,
    isSubmitting: state => state.isSubmitting,
    isRunQuery: state => state.isRunQuery,
    isCancelled: state => state.isCancelled,
    onFly: state => state.onFly,
    sorgulanmaDurumu: state => state.sorgulanmaDurumu,
    durumAciklamasi: state => state.durumAciklamasi,
    vekaletCache: state => state.vekaletCache,
    parentTaskId: state => state.parentTaskId,
    lastParsedXmlSignature: state => state.lastParsedXmlSignature || '',
    lastParsedFormDataSignature: state => state.lastParsedFormDataSignature || '',
    hashSilinenTempIdler: state => state.hashSilinenTempIdler || [],
    tarafHatalari: state => {
        const hatalar: string[] = [];
        if (!state.dosyalar.length) return hatalar;

        state.dosyalar.forEach((dosya: any) => {
            if (!dosya.parsedFile) return;

            // Alacaklı ölüm kaydı kontrolü
            const olusAlacaklilar = dosya.parsedFile.tarafList?.filter(
                (t: any) =>
                    t.isAlacakli() &&
                    t.kisiMi &&
                    t.temelBilgileri?.olumKaydi === true
            ) || [];

            if (olusAlacaklilar.length > 0) {
                hatalar.push(
                    `#${dosya.tempId}: Alacaklı ölüm kaydı tespit edildi - Takip açılamaz`
                );
            }

            // Borçlu hataları
            const borclular = dosya.parsedFile.tarafList?.filter(
                (t: any) => t.isBorclu()
            ) || [];

            borclular.forEach((borclu: any) => {
                if (borclu?.kisiMi && borclu?.temelBilgileri?.olumKaydi === true) {
                    const adi = borclu.temelBilgileri?.adi ?? '';
                    const soyadi = borclu.temelBilgileri?.soyadi ?? '';
                    const name = `${adi} ${soyadi}`.trim() || borclu.adSoyad || 'Bilinmeyen';
                    hatalar.push(`#${dosya.tempId}: Ölü borçlu - ${name}`);
                }

                if (borclu?.kisiMi && borclu?.temelBilgileri?.tcKimlikHatalimi === true) {
                    const adi = borclu.temelBilgileri?.adi ?? '';
                    const soyadi = borclu.temelBilgileri?.soyadi ?? '';
                    const name = `${adi} ${soyadi}`.trim() || borclu.adSoyad || 'Bilinmeyen';
                    hatalar.push(`#${dosya.tempId}: Hatalı TC - ${name}`);
                }

                if (!borclu.kisiMi) {
                    // Kurum için Mersis/Vergi no ve ünvan kontrolü
                    const mersisBosMu = !borclu.temelBilgileri?.mersis || borclu.temelBilgileri.mersis.trim() === '';
                    const vergiBosMu = !borclu.temelBilgileri?.vergiNo || borclu.temelBilgileri.vergiNo.trim() === '';
                    const kurumAdiYok = !((borclu.temelBilgileri && borclu.temelBilgileri.kurumAdi) || borclu.kurumAdi);

                    if (mersisBosMu && vergiBosMu && kurumAdiYok) {
                        const kurumAdi = borclu.adSoyad || borclu.kurumAdi || borclu.temelBilgileri?.kurumAdi || 'Bilinmeyen kurum';
                        hatalar.push(`#${dosya.tempId}: Bu bilgilerde Kurum bulunamadı - ${kurumAdi}`);
                    }
                }
            });
        });

        return hatalar;
    },

    vekaletCheck: state => {
        if (!state.dosyalar.length) return [];
        const hataliDosyalar: number[] = [];
        state.dosyalar.forEach(dosya => {
            if (!dosya.parsedFile?.vekalet && !dosya.parsedFile?.vekaletObject?.id) {
                // @ts-ignore
                hataliDosyalar.push(dosya.tempId);
            }
        });
        return hataliDosyalar;
    },

    alacakliProblemVarMi: state => {
        try {
            return state.dosyalar && state.dosyalar.length > 0 && state.dosyalar.some(dosya => {
                return dosya.parsedFile?.tarafList
                    ?.filter((taraf: any) => taraf.tarafSifati.rolID === 21)
                    ?.some((alacakli: any) => {
                        if (alacakli.kisiMi && alacakli.temelBilgileri?.olumKaydi === true) return true;
                        if (alacakli.kisiMi && alacakli.temelBilgileri?.tcKimlikHatalimi === true) return true;
                        if (!alacakli.kisiMi && !((alacakli.temelBilgileri && alacakli.temelBilgileri.kurumAdi) || alacakli.kurumAdi)) return true;
                        return false;
                    });
            });
        } catch (e) {
            console.error('alacakliProblemVarMi hesaplarken hata:', e);
            return false;
        }
    },

    step2Stats: state => {
        let toplamHarcMasraf = 0;
        let toplamTutar = 0;

        state.dosyalar.forEach(dosya => {
            if (dosya.toplamTakibeEsasMiktar) {
                toplamHarcMasraf += Number(dosya.parsedFile.harcMasrafToplam) || 0;
                toplamTutar += Number(dosya.parsedFile.toplamTakibeEsasMiktar) || 0;
            }
        });

        return [
            {
                label: 'Toplam Dosya',
                value: state.dosyalar.length,
                format: 'number' as const,
                icon: 'mdi-account-outline',
                iconColor: '#FF9500',
                borderColor: '#FF9500',
                bgColor: 'rgba(255, 149, 0, 0.1)',
            },
            {
                label: 'Toplam Harç/Masraf',
                value: toplamHarcMasraf,
                format: 'currency' as const,
                icon: 'mdi-cash',
                iconColor: '#3B82F6',
                borderColor: '#3B82F6',
                bgColor: 'rgba(59, 130, 246, 0.1)',
            },
            {
                label: 'Toplam Tutar',
                value: toplamTutar,
                format: 'currency' as const,
                icon: 'mdi-currency-try',
                iconColor: '#10B981',
                borderColor: '#10B981',
                bgColor: 'rgba(16, 185, 129, 0.1)',
            },
        ];
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
